import { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// Retry configuration for the comprehensive batch call.
export const MAX_BATCH_RETRIES = 2;
export const BATCH_RETRY_BASE_DELAY_MS = 1000;

// Retry only transient failures: timeouts, network errors (no response), and 5xx.
// Auth failures (401/403) must not retry, so the caller's sign-out path still runs.
// Cancellations must not retry: the same AbortSignal would abort every retry and
// each attempt spams the error tracker with a "Network Error" log.
export const isRetryableBatchError = (error: unknown): boolean => {
  // ponytail: mirrors axios.isCancel (checks __CANCEL__). Cancellations have
  // no response, so without this they'd fall through to the "network error"
  // branch below and the same aborted signal would be reused for 3 attempts.
  if ((error as { __CANCEL__?: boolean })?.__CANCEL__) return false;
  const axiosError = error as AxiosError;
  if (axiosError?.code === 'ECONNABORTED') return true; // timeout
  const status = axiosError?.response?.status;
  if (status === undefined) return true; // network error, no response
  return status >= 500;
};

// POST to a URL with exponential backoff on transient errors.
export async function postBatchWithRetry<T>(
  client: AxiosInstance,
  url: string,
  body: unknown,
  config: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= MAX_BATCH_RETRIES; attempt++) {
    try {
      return await client.post<T>(url, body, config);
    } catch (error) {
      lastError = error;
      if (attempt === MAX_BATCH_RETRIES || !isRetryableBatchError(error)) {
        throw error;
      }
      const delay = BATCH_RETRY_BASE_DELAY_MS * Math.pow(2, attempt);
      console.warn(`[Dashboard] Batch call failed (attempt ${attempt + 1}/${MAX_BATCH_RETRIES + 1}), retrying in ${delay}ms`, error);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  throw lastError;
}
